import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { trackByRef } from 'src/app/lego-utils';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, shareReplay, switchMap, tap } from 'rxjs/operators';
import { TaskListHeader } from 'src/app/models/domain';
import { ControlSpecificationService, DesignService, IPage, PagedCollection } from 'src/app/state';
import { CONTROL_SPECIFICATION_PAGINATOR } from './control-specification-pagination.provider';

@Component({
  selector: 'em-control-specification',
  templateUrl: './control-specification.component.html',
  standalone: false,
})
export class ControlSpecificationComponent implements OnDestroy, OnInit {
  public pagination$?: Observable<IPage<TaskListHeader>>;
  public controlSpecificationUiState$ = this.controlSpecificationService.uiById$;

  public designLoading$ = this.designService.loading$;
  public controlSpecificationsLoading$ = this.paginatorRef.loading$;
  public error$ = this.controlSpecificationService.error$;
  public perPage = 10;
  public pageIndex = 1;
  public readonly trackByRef = trackByRef;

  private readonly showAll$ = new BehaviorSubject(false);
  private readonly perPage$ = new BehaviorSubject(10);
  private readonly subscriptions = new Subscription();

  /**
   * The design is part of the cache key, not just showAll/perPage: `PagedCollection` keys
   * pages by number alone, so without this, switching design would serve the previous
   * design's page 1 and never re-request. Akita got this from `store.reset()` emptying the
   * entity store its `selectPage()` re-hydrated from — a second invalidation path that no
   * longer exists.
   *
   * Resetting to page 1 too, since page N of the old design may not exist in the new one.
   */
  private readonly shouldClearCacheWhenChanged$ = combineLatest([
    this.showAll$,
    this.perPage$,
    // selectDesignId(), not designId$: the latter filters out undefined, which would stall
    // the first fetch until a design loads.
    this.designService.selectDesignId().pipe(distinctUntilChanged()),
  ]).pipe(
    tap(() => {
      this.paginatorRef.clearCache();
      this.paginatorRef.setPage(1);
    })
  );

  public constructor(
    private readonly designService: DesignService,
    private readonly controlSpecificationService: ControlSpecificationService,

    @Inject(CONTROL_SPECIFICATION_PAGINATOR)
    public paginatorRef: PagedCollection<TaskListHeader>
  ) {}

  public ngOnInit() {
    this.pagination$ = combineLatest([
      this.paginatorRef.pageChanges,
      this.shouldClearCacheWhenChanged$,
    ]).pipe(
      switchMap(([page, [showAll, perPage]]) => {
        const req = showAll
          ? () => this.controlSpecificationService.getAll()
          : () =>
              this.controlSpecificationService.getPage({
                page,
                perPage,
              });

        return this.paginatorRef.getPage(req);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  public expansionChange(isExpanded: boolean, taskListHeader: TaskListHeader) {
    if (!isExpanded) {
      this.controlSpecificationService.updateUiState(taskListHeader.id, {
        isExpanded,
        loading: false,
      });

      return;
    }

    this.subscriptions.add(
      this.controlSpecificationService
        .populateOperationsByTaskListHeader(taskListHeader)
        .subscribe()
    );
  }

  public perPageChange(perPage: number) {
    this.perPage$.next(perPage);
  }

  public showAllSpecifications(showAll: boolean) {
    this.showAll$.next(showAll);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
